import { type RefObject, useEffect, useRef } from "react";

/**
 * Calls `onView` the first time at least half of the element is visible.
 * Re-arms whenever `enabled` turns off, so one modal open counts once.
 *
 * @param {RefObject<Element | null>} ref - Element to watch
 * @param {() => void} onView - Called once per enabled period
 * @param {boolean} enabled - False stops watching and re-arms
 * @returns {void}
 */
const useOnceInView = (
	ref: RefObject<Element | null>,
	onView: () => void,
	enabled: boolean,
): void => {
	const hasFiredRef = useRef(false);
	const onViewRef = useRef(onView);
	onViewRef.current = onView;

	useEffect(() => {
		if (!enabled) {
			hasFiredRef.current = false;
			return;
		}

		const element = ref.current;
		if (!element || typeof IntersectionObserver === "undefined") return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (hasFiredRef.current) return;
				if (!entries.some((entry) => entry.isIntersecting)) return;

				hasFiredRef.current = true;
				observer.disconnect();
				onViewRef.current();
			},
			{ threshold: 0.5 },
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, [enabled, ref]);
};

export default useOnceInView;
